import { useState, useEffect } from "react";
import { useTranslation } from "next-i18next";
import upperFirst from "lodash/upperFirst";
import { useRouter } from "next/router";

import { Notifications as NotificationsUI } from "common/ui/Notifications";
import { ChatRequestResponseModal } from "common/features/chat/components/ChatRequestResponseModal";
import { DealCancelledModal } from "common/components/DealCancelledModal";

import { markNotificationAsRead } from "common/features/userNotifications/api/markNotificationAsRead";
import { markAllNotificationsAsRead } from "common/features/userNotifications/api/markAllNotificationsAsRead";
import { useUserNotifications, NotificationType } from "common/features/userNotifications/api/getUserNotifications";
import { deleteUserNotifications } from "common/features/userNotifications/api/deleteUserNotifications";
import { UserType } from "common/middleware/api.services";
import { Logger } from "common/middleware/logger";

import { APP_ROUTES } from "common/constants/routes";

export default function Notifications() {
  const { t } = useTranslation("common");
  const { query, replace, locale } = useRouter();
  const { notifications, mutateNotifications } = useUserNotifications({ statusFilter: "unread" });

  const [isClearingNotifications, setIsClearingNotifications] = useState(false);
  const [chatRequest, setChatRequest] = useState<{
    notificationId: string,
    opportunityId: string,
    sender: string,
    message: string,
  }>();
  const [dealCancelledModal, setDealCancelledModal] = useState<{
    listingId: number,
    cancelledBy: string,
    notificationId: string,
  }>();

  useEffect(() => {
    if (!notifications || !query?.notificationId) {
      return;
    }

    const notification = notifications.find(n => n.id === query.notificationId);

    if (query?.rejoinRequested) {
      setChatRequest({
        notificationId: notification?.id,
        opportunityId: notification?.metadata.opportunityId,
        sender: notification?.metadata.requesterName,
        message: notification?.metadata.message,
      });
      delete query.rejoinRequested;
      delete query.notificationId;
      replace(query, undefined, { shallow: true });
      return;
    }
  }, [query, notifications]);

  const markAsRead = async (notificationId: string) => {
    await markNotificationAsRead(notificationId);
    await mutateNotifications();
  };

  const getDestination = (targetUserType: UserType | undefined, path: string) => {
    if (!targetUserType) {
      return path;
    }

    const destinationUrl = UserType[upperFirst(targetUserType.toString())] === UserType.Seller
      ? process.env.NEXT_PUBLIC_SELLER_URL
      : process.env.NEXT_PUBLIC_BUYER_URL;
    const isCurrentSameAsDestination = location.origin === destinationUrl;

    return isCurrentSameAsDestination ? path : `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${destinationUrl}${path}`;
  };

  const notificationsData = notifications?.map(n => {
    const notificationType = NotificationType[upperFirst(n.type.toString())];

    const notification = {
      id: n.id,
      subject: n.subject,
      message: n.message,
      date: n.createdDateTimeUtc,
      onClick: undefined,
      href: undefined,
    };

    switch (notificationType) {
      case NotificationType.ListingIncompleteReminder:
      case NotificationType.ListingDenied:
      case NotificationType.ListingChangesDenied:
        notification.href = getDestination(n.targetUserType, "/listings?view=draft");
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.ListingApproved:
      case NotificationType.ListingChangesAccepted:
        notification.href = getDestination(n.targetUserType, "/listings?view=published");
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.OfferNewReceived:
      case NotificationType.OfferAcceptedSeller:
      case NotificationType.OfferExpiring:
      case NotificationType.OfferExpired:
      case NotificationType.OfferDeclined:
      case NotificationType.OfferExtensionReceived:
      case NotificationType.OfferExtensionApproved:
      case NotificationType.OfferExtensionDeclined:
      case NotificationType.OpportunityCreated:
      case NotificationType.OfferCancelledNotice:
      case NotificationType.OfferAcceptedBuyer:
      case NotificationType.OpportunityInviteReceived:
      case NotificationType.ListingChangesBuyerUpdate:
      case NotificationType.DealInviteBuyer:
      case NotificationType.DealBuyerSignatureRequired:
      case NotificationType.DealSellerSignatureRequired:
        notification.href = getDestination(n.targetUserType, `/opportunities/${n.metadata.opportunityId}`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.DealSignedComplete:
      case NotificationType.DealCanceledConfirmation:
      case NotificationType.DealSetToFirm:
        notification.href = getDestination(n.targetUserType, `/deals/${n.metadata.dealId}/financial-summary`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.DealCanceledNotice:
        notification.href = getDestination(n.targetUserType, `/deals/${n.metadata.dealId}/financial-summary`);
        notification.onClick = () => setDealCancelledModal({
          listingId: n.metadata.listingId,
          cancelledBy: n.metadata.cancellationInitiatorName,
          notificationId: n.id,
        });
        break;
      case NotificationType.SignScanned:
        notification.href = getDestination(n.targetUserType, `/listings/${n.metadata.property_id}`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.ListingInviteSeller:
        notification.href = getDestination(n.targetUserType, `/listings?view=draft&propertyId=${n.metadata.listingId}&verifyListing=true&notificationId=${n.id}`);
        break;
      case NotificationType.ChatRejoinRequested:
        notification.href = getDestination(n.targetUserType, `/chat?rejoinRequested=true&notificationId=${n.id}`);
        break;
      case NotificationType.CancellationContractBuyerSignatureRequired:
      case NotificationType.CancellationContractSellerSignatureRequired:
        notification.href = getDestination(n.targetUserType, `/deals/${n.metadata.dealId}/cancel-deal/contract`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.FulfillmentContractSellerSignatureRequired:
        notification.href = getDestination(n.targetUserType, `/deals/${n.metadata.dealId}/fulfilment-notice/contract`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.FulfillmentContractFullySigned:
        notification.href = getDestination(n.targetUserType, `/deals/${n.metadata.dealId}/documents`);
        notification.onClick = async () => await markAsRead(n.id);
        break;
      case NotificationType.AppointmentRequested:
      case NotificationType.AppointmentAccepted:
      case NotificationType.AppointmentDeclined:
      case NotificationType.AppointmentRescheduleProposed:
      case NotificationType.AppointmentRescheduleAccepted:
      case NotificationType.AppointmentRescheduleDeclined:
      case NotificationType.AppointmentCancelled:
      case NotificationType.AppointmentReminder:
      case NotificationType.OpenHouseUpdated:
      case NotificationType.OpenHouseCancelled:
      case NotificationType.OpenHouseNewRsvp:
      case NotificationType.OpenHouseRsvpCancelled:
        notification.href = `${process.env.NEXT_PUBLIC_AUTH_URL}/${locale}${APP_ROUTES.APPOINTMENTS}`;
        notification.onClick = async () => await markAsRead(n.id);
        break;
      default:
        Logger.debug("Unknown notification type", n.type);
        break;
    }

    return notification;
  });

  return (
    <>
      <NotificationsUI
        icon={<Icon />}
        totalNumOfNotifications={notifications?.length}
        action={{
          label: t("notifications.clearAllNotifications"),
          onClick: async () => {
            setIsClearingNotifications(true);
            await markAllNotificationsAsRead();
            await mutateNotifications();
            setIsClearingNotifications(false);
          },
          isLoading: isClearingNotifications,
          isDisabled: notifications?.length === 0,
        }}
      >
        {notificationsData?.map(n => (
          <NotificationsUI.Item
            key={n.id}
            href={n.href}
            onClick={n.onClick}
            subject={n.subject}
            message={n.message}
            date={n.date}
            clearNotification={async () => {
              await markNotificationAsRead(n.id);
              await mutateNotifications();
            }}
          />
        ))}
      </NotificationsUI>
      {chatRequest && (
        <ChatRequestResponseModal
          close={() => setChatRequest(undefined)}
          opportunityId={chatRequest.opportunityId}
          senderName={chatRequest.sender}
          message={chatRequest.message}
          onSuccess={async () => {
            await deleteUserNotifications([chatRequest.notificationId]);
            await mutateNotifications();
          }}
        />
      )}
      {dealCancelledModal && (
        <DealCancelledModal
          close={() => setDealCancelledModal(undefined)}
          listingId={dealCancelledModal.listingId}
          cancelledBy={dealCancelledModal.cancelledBy}
          onAcknowledge={async () => await markAsRead(dealCancelledModal.notificationId)}
        />
      )}
    </>
  )
}

const Icon = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 0C10.5523 0 11 0.447716 11 1V3L10.9995 3.0317L10.9986 3.05341L10.9966 3.0824C13.836 3.55709 16 6.02583 16 9C16 12.0933 16.625 13.312 17.5987 15.2103L17.6333 15.2781C17.803 15.6088 17.5628 16 17.1909 16H2.80911C2.43728 16 2.19705 15.6088 2.36673 15.2781L2.4014 15.2103C3.37503 13.312 4.00003 12.0933 4.00003 9C4.00003 6.02583 6.16409 3.55709 9.00345 3.0824L9.00125 3.04284C9.00052 3.02863 9.00003 3.01435 9.00003 3V1C9.00003 0.447716 9.44778 0 10 0Z" fill="#5C5F62"/>
      <path d="M12 18C12 19.1046 11.1045 20 10 20C8.89554 20 8.00003 19.1046 8.00003 18H12Z" fill="#5C5F62"/>
    </svg>
  )
}
